import { basicBlockActions } from 'articleEditor/utils/actions';
import { findNodePath } from 'lib-knowledge-document-structure/plate/common';
import {
  TableProvider,
  useTableElement,
  useTableElementState,
} from 'lib-knowledge-document-structure/plate/table';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyEditor,
  MyTableElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';
import { RenderElementProps } from 'lib-knowledge-document-structure/slate/react';
import { useMemo } from 'react';

import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';
import {
  getHasColumnHeader,
  getHasHeaderRow,
  toggleColumnHeader,
  toggleTableHeader,
} from './tableUtils';
import { TableCellHoverProvider } from './useCellHover';

type Props = RenderElementProps & {
  element: MyTableElement;
};

export function TableBlock(props: Props): JSX.Element {
  const editor = useMyEditorRef();
  const path = findNodePath(editor, props.element);
  if (!path) throw new Error('this cant happen, path should always be defined');

  const blockActions = useMemo(
    () => createBlockActions({ editor, path, element: props.element }),
    [editor, props.element, path],
  );

  return (
    <TableProvider>
      <TableCellHoverProvider>
        <SortableBlockWrapper
          className={s['blockTable']}
          element={props.element}
          blockActions={blockActions}
        >
          <TableElement {...props} />
        </SortableBlockWrapper>
      </TableCellHoverProvider>
    </TableProvider>
  );
}

// Created sub component so it is inside the TableProvider
function TableElement({ attributes, children }: Props): JSX.Element {
  const { props: tableProps, colGroupProps } = useTableElement();
  const { colSizes, minColumnWidth } = useTableElementState();

  return (
    <table {...attributes} {...tableProps} className={s['blockContent']}>
      <colgroup {...colGroupProps}>
        {colSizes.map((width, index) => (
          <col
            key={index}
            style={{
              minWidth: minColumnWidth,
              width: width || undefined,
            }}
          />
        ))}
      </colgroup>
      <tbody>{children}</tbody>
    </table>
  );
}

function createBlockActions({
  editor,
  element,
  path,
}: {
  editor: MyEditor;
  element: MyTableElement;
  path: Path;
}) {
  const hasHeaderRow = getHasHeaderRow(element);
  const hasColumnHeader = getHasColumnHeader({
    editor,
    element,
    path,
  });

  return [
    ...basicBlockActions,
    { key: 'heading', label: 'Headings' },
    {
      key: 'header-row',
      icon: 'th' as const,
      label: 'Header row',
      isSelected: hasHeaderRow,
      action: () => {
        toggleTableHeader({
          editor,
          path,
          element,
        });
      },
    },
    {
      key: 'header-column',
      icon: 'th' as const,
      label: 'Header column',
      isSelected: hasColumnHeader,
      action: () => {
        toggleColumnHeader({
          editor,
          path,
          element,
        });
      },
    },
  ] as const;
}
